(ns app.front.view.group.admin.fees.template.template-core
  (:require [app.domain.fee.model :as fee-model]
            [app.domain.fee.rule-check :as rule-check]
            [app.front.ui :as ui]
            [app.front.ui.column-select :as columns]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.money-input :as mi]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.fees.formula.formula-core :as formula]
            [app.front.view.group.admin.fees.group.group-core :as fg-core]
            [app.front.view.group.admin.fees.template.template-events :as ft-events]
            [app.front.view.group.admin.fees.template.template-subs :as ft-subs]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.system-of-units.toggle :as sou-toggle]
            [core.domain.system-of-units :as sou]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


;;; Init


(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ fee-schedule disabled? close-callback-event]]
    {:db (-> db
             (dissoc ::ft-events/fee-templates)
             (assoc-in [::ft-events/fee-templates :disabled?] disabled?)
             (assoc-in [::ft-events/fee-templates :fee-schedule] fee-schedule)
             (assoc-in [::ft-events/fee-templates :close-callback-event] close-callback-event))}))


;; Components


(defn change-fee-group
  "Unselects the fee group when clicking a group that was already selected"
  [fee-template-id old-group-id new-group-id]
  (if (= new-group-id old-group-id)
    (>evt [::ft-events/change-field fee-template-id :fee-template/group nil])
    (>evt [::ft-events/change-field fee-template-id :fee-template/group new-group-id])))


(defn- help-button [tr]
  [ui/button {:on-click   (fn [_]
                            (-> js/document
                                (.getElementById "functions-help")
                                (.scrollIntoView #js {:behavior "smooth"}))
                            nil)
              :kind       :tertiary
              :size       :small
              :class-name "-mt-2 -mb-2"}
   (tr [:fees/help])])


(defn render-title [{:keys [id tr disabled? titles]}]
  [:div.col-span-2
   [ui-i18n/edit-language-selector
    {:allowed-locales-sub ::gas/allowed-locales}]
   [ui-i18n/localized-string-edit
    {:id                  (str "template-title-" id)
     :label-text          (tr [:fees/template-title])
     :disabled            disabled?
     :strings             titles
     :required            true
     :on-change           #(rf/dispatch-sync [::ft-events/change-field id :fee-template/titles %])
     :allowed-locales-sub ::gas/allowed-locales}]])

(defn- render-phase [{:keys [id tr phase disabled?]}]
  [ui-select/select {:id           (str "phase-" id)
                     :class-name   "full-width-select"
                     :label-text   (tr [:fees/category])
                     :required     true
                     :disabled     disabled?
                     :allow-empty? false
                     :values       fee-model/phases
                     :value        phase
                     :on-change    #(>evt [::ft-events/change-field id :bill/phase %])
                     :val->str     (comp tr vector)}])


(defn- render-price-formation-type [{:keys [tr disabled? id type]}]
  [:div
   (cc/form-label {:class-name (if disabled? "text-gray-400" "required-field")}
                  (tr [:fees/price-formation]))
   [:div.mt-1.flex
    (cc/radio-button {:label-text (tr [:fee-template/fixed])
                      :class-name "mt-2"
                      :value      "fixed"
                      :name       "calculation"
                      :disabled   disabled?
                      :checked    (= type :fee-template/fixed)
                      :on-change  #(>evt [::ft-events/change-field id :fee-template/type :fee-template/fixed])})
    (cc/radio-button {:label-text (tr [:fee-template/both-calculated])
                      :class-name "mt-2"
                      :value      "both-calculated"
                      :name       "calculation"
                      :disabled   disabled?
                      :checked    (= type :fee-template/both-calculated)
                      :on-change  #(>evt [::ft-events/change-field id :fee-template/type :fee-template/both-calculated])})]])


(def editor-grid :div.p-4.bg-white.grid.grid-cols-2.gap-4.mb-4)
(def editor-title :h2.text-sm.font-bold.p-0.leading-tight)
(def editor-title-required :h2.text-sm.font-bold.p-0.leading-tight.required-field)

(defn- render-quantity-formula [{:keys [tr disabled? id quantity-formula fee-variables]}]
  [editor-grid
   [:div.col-span-2.flex.justify-between.items-start
    [editor-title (tr [:fees/quantity-formula])]
    [help-button tr]]
   [formula/editor {:entity-id (str "template-quantity-formula-" id)
                    :value     quantity-formula
                    :disabled? disabled?
                    :variables fee-variables
                    :tr        tr
                    :on-change #(rf/dispatch-sync [::ft-events/change-formula id :fee-template/quantity-formula %])}]
   [formula/tester {:entity-id id
                    :field     :fee-template/quantity-formula
                    :formula   quantity-formula
                    :variables fee-variables
                    :tr        tr}]])


(defn- render-unit-price-formula [{:keys [tr disabled? id unit-price-formula fee-variables]}]
  [editor-grid
   [:div.col-span-2.flex.justify-between.items-start
    [editor-title-required (tr [:fees/unit-price-formula])]
    [help-button tr]]
   [formula/editor {:entity-id (str "template-unit-price-formula-" id)
                    :value     unit-price-formula
                    :disabled? disabled?
                    :variables fee-variables
                    :tr        tr
                    :on-change #(rf/dispatch-sync [::ft-events/change-formula id :fee-template/unit-price-formula %])}]
   [formula/tester {:entity-id id
                    :field     :fee-template/unit-price-formula
                    :formula   unit-price-formula
                    :variables fee-variables
                    :tr        tr}]])


(defn- render-unit-settings [{:keys [tr disabled? id unit]}]
  (let [saved-unit-system   (when unit (-> (sou/unit-info-by-label unit)
                                           last
                                           :system-of-units))
        quantity-variable   (<sub [::ft-subs/first-quantity-variable])
        quantity-unit       (<sub [::ft-subs/form-unit quantity-variable])
        price-variable      (<sub [::ft-subs/first-unit-price-variable])
        price-ok?           (rule-check/price-ok-for-unit? (:formula (<sub [::ft-subs/unit-price-formula]))
                                                           (<sub [::ft-subs/form-unit price-variable]))
        default-unit-system (<sub [::sou-toggle/default-system-of-units])
        creating-new?       (= id :new)
        unit-enabled?       (<sub [::ft-subs/unit-enabled?])
        toggled?            (if (some? unit-enabled?)
                              unit-enabled?
                              (or (some? unit) creating-new?))
        visible?            (and quantity-unit
                                 (= 1 (<sub [::ft-subs/quantity-formula-variable-count]))
                                 price-ok?)]
    [editor-grid
     [:div.col-span-2.flex.justify-between.items-start
      [editor-title (tr [:fees/unit-settings])]
      [help-button tr]]
     (if visible?
       [:div.flex.flex-col
        (cc/toggle {:id         "unit-visibility-toggle"
                    :label-text (tr [:fees/unit-toggle-label]
                                    [quantity-variable (:label (sou/unit-info quantity-unit default-unit-system))])
                    :labelA     (tr [:fees/unit-disabled])
                    :labelB     (tr [:fees/unit-enabled])
                    :disabled   disabled?
                    :toggled    toggled?
                    :on-toggle  #(>evt [::ft-events/toggle-unit-use id %])})
        (when (<sub [::sou-toggle/multiple-unit-systems?])
          [ui-radio/radio {:name       "systems-of-unit"
                           :on-change  #(>evt [::ft-events/change-field id :fee-template/system-of-units %])
                           :value      (or (<sub [::ft-subs/unsaved-system-of-units])
                                           saved-unit-system
                                           default-unit-system)
                           :val->str   #(tr [%])
                           :label-text (tr [:fees/system-of-units])
                           :disabled   (or disabled?
                                           (not toggled?))
                           :class      "mt-4"
                           :values     (<sub [::sou-toggle/available-unit-systems])}])]
       [:p "-"])]))


(defn- render-fixed-formula [{:keys [tr disabled? id unit-price-formula]}]
  [:div.p-4.bg-white.mb-4
   [editor-title-required {:class "mb-3"} (tr [:fees/fixed-total])]
   [ui/text-input {:id        (str "template-fixed-total" id)
                   :value     (:formula unit-price-formula)
                   :disabled  disabled?
                   :on-change #(rf/dispatch-sync [::ft-events/change-formula id :fee-template/unit-price-formula (futil/tval %)])}]])


(defn- render-help [tr fee-variables]
  [:div#functions-help.p-4.bg-white.grid.grid-cols-2.gap-4.mb-4
   [editor-title (tr [:fees/help])]
   [formula/variable-list tr fee-variables]
   [formula/functions-help tr]
   (when (<sub [::sou-toggle/multiple-unit-systems?])
     [:div.col-span-2
      [:h2.mb-2.text-sm
       [:span.font-bold (str (tr [:fees/unit-settings]) ":")]
       [:span " " (tr [:fees/system-of-units-help])]]])])


(defn- render-external-id [{:keys [fee-template id tr disabled?]}]
  [ui/text-input {:id         (str "template-external-fee-code" id)
                  :label-text (tr [:fees/external-fee-code]
                                  [(<sub [::gas/backend-system-name :fees/integration])])
                  :disabled   disabled?
                  :value      (:fee-template/external-fee-code fee-template)
                  :class      "mb-4"
                  :required   true
                  :on-change  #(rf/dispatch-sync [::ft-events/change-field id :fee-template/external-fee-code (futil/tval %)])}])

(defn set-date-rule-fn [id]
  (fn [value]
    (>evt [::ft-events/change-field id :fee-template/date-rule value])))

(defn late-fee-attrs [{:keys [tr disabled? id fee-template]}]
  (let [controls-enabled (-> fee-template :fee-template/date-rule some?)
        set-date-rule    (set-date-rule-fn id)]
    [:div.col-span-2
     [:div.flex.gap-8.w-full.justify-between
      (cc/checkbox {:id         (str id "-late")
                    :disabled   disabled?
                    :label-text (tr [:fee-template/late-fee])
                    :checked    controls-enabled
                    :on-change  #(set-date-rule (when-not controls-enabled
                                                  :fee-template.date-rule/one-time))})]
     ;; no selector for :fee-template/date-reference due to there only being one option
     [:div.mt-2
      [ui-radio/radio {:values     fee-model/fee-date-rules
                       :name       (str id "-date-rule")
                       :disabled   (or disabled? (not controls-enabled))
                       :label-text (tr [:fee-template/date-rule])
                       :val->str   #(tr [%])
                       :value      (:fee-template/date-rule fee-template)
                       :on-change  set-date-rule}]]]))

(defn- render-advanced-options [{:keys                                                 [id group-id tr disabled? fee-integration?]
                                 {:fee-template/keys [minimum-total
                                                      allow-including-in-property-tax?
                                                      general-ledger-code
                                                      refundable?
                                                      work-order-number?]
                                  :as                fee-template}                     :fee-template}]
  (r/with-let [*advanced-options-expanded? (r/atom false)]
    [:<>
     [:div.col-span-2
      [ui/expand-button {:expand-handler  #(swap! *advanced-options-expanded? not)
                         :tr              tr
                         :expanded?       @*advanced-options-expanded?
                         :title-expanded  (tr [:fees/hide-advanced-options])
                         :title-collapsed (tr [:fees/show-advanced-options])}]]
     (when @*advanced-options-expanded?
       [:<>
        (when (<sub [::gas/has-permit-type? #{:permit-type/L}])
          [late-fee-attrs (util/mmap id tr disabled? fee-template)])
        ;; Group
        [fg-core/fee-group-select (util/mmap {:on-change         #(change-fee-group id group-id %)
                                              :selected-group-id group-id}
                                             tr disabled?)]
        [:div
         ;; Minimum total
         [mi/money-input
          {:id            (str "template-minimum-total" id)
           :default-value minimum-total
           :label-text    (tr [:fees/minimum-total])
           :disabled      disabled?}
          #(rf/dispatch-sync [::ft-events/change-field id :fee-template/minimum-total %])]

         ;; Allow including in property tax
         [:div.mt-4
          (cc/checkbox {:id         (str "allow-including-in-property-tax-" id)
                        :label-text (tr [:fees/allow-including-in-property-tax])
                        :checked    (boolean allow-including-in-property-tax?)
                        :disabled   disabled?
                        :on-change  #(>evt [::ft-events/change-field id :fee-template/allow-including-in-property-tax? %])})]

         ;; Refundable
         [:div.mt-4
          (cc/checkbox {:id         (str "refundable-" id)
                        :label-text (tr [:fees/refundable])
                        :checked    (boolean refundable?)
                        :disabled   disabled?
                        :on-change  #(>evt [::ft-events/change-field id :fee-template/refundable? %])})]

         [:div.mt-4
          (cc/checkbox {:id         (str "work-order-number-" id)
                        :label-text (tr [:fees/work-order-setting-label])
                        :checked    (boolean work-order-number?)
                        :disabled   disabled?
                        :on-change  #(>evt [::ft-events/change-field id :fee-template/work-order-number? %])})]

         ;; Bond payment
         (when fee-integration?
           [:div.mt-4
            (cc/checkbox {:id         (str "bond-payment-id" id)
                          :label-text (tr [:fees/bond-payment])
                          :checked    (boolean (:fee-template/bond-payment? fee-template))
                          :disabled   disabled?
                          :on-change  #(>evt [::ft-events/change-field id :fee-template/bond-payment? %])})])

         ;; General Ledger code
         [:div.mt-4
          [ui/text-input {:id         (str "gl-code" id)
                          :type       :text
                          :label-text (tr [:fees/general-ledger-code])
                          :disabled   disabled?
                          :value      general-ledger-code
                          :on-change  #(rf/dispatch-sync [::ft-events/change-field
                                                          id
                                                          :fee-template/general-ledger-code
                                                          (futil/tval %)])}]]]])]))


(defn- render-save-cancel [{:keys [id tr creating-new? disabled?]}]
  (let [save-disabled?   (<sub [::ft-subs/save-disabled?])
        cancel-disabled? (and (not creating-new?) save-disabled?)]
    [eb/error-boundary
     (when-not disabled?
       [:div.col-start-2.flex.justify-end.gap-2
        (when-not creating-new?
          [ui/modal-confirmation-button
           {:tr                  tr
            :id                  "delete-fee-template"
            :on-yes-click        #(>evt [::ft-events/delete id])
            :trigger-button-text (tr [:delete])
            :trigger-button-opts {:kind :ghost}}
           (tr [:fees/confirm-delete-fee-template])])
        [ui/modal-confirmation-button
         {:tr                  tr
          :id                  "cancel-fee-template-editing"
          :on-yes-click        #(>evt [::ft-events/cancel id])
          :trigger-button-text (tr [:cancel])
          :trigger-button-opts {:kind     :tertiary
                                :disabled cancel-disabled?}}
         (tr [:fees/confirm-cancel-fee-template-editing])]

        (cc/button {:disabled (boolean save-disabled?)
                    :on-click #(>evt [::ft-events/save id])}
                   (tr [(if creating-new? :create :save)]))])]))

(defn- divider []
  [:div.col-span-2.border.border-solid.border-gray-300.my-4])

(defn field-filled?
  "Returns true if `value` is not an empty string or nil."
  [value]
  (some? (str-util/blank->nil value)))

(defn render-penalty
  [{:keys [tr id fee-template disabled?]}]
  (let [allow-penalty?         (boolean (:fee-template/allow-penalty? fee-template))
        penalty-rate-filled?   (field-filled? (:fee-template/penalty-rate fee-template))
        interest-filled?       (field-filled? (:fee-template/interest-rate fee-template))
        primary-permit-type    (<sub [::gas/group-primary-permit-type])
        supports-grace-period? (<sub [::gas/permit-type-supports-grace-period? primary-permit-type])]
    [:div.border.border-solid.border-gray-300.p-4.col-span-2
     [:div
      (cc/checkbox {:id         (str "allow-penalty-" id)
                    :label-text (tr [:fees/allow-penalty])
                    :checked    allow-penalty?
                    :disabled   disabled?
                    :on-change  #(>evt [::ft-events/allow-penalty-check id %])})]
     (when allow-penalty?
       [:<>
        [divider]
        [:div
         [:div.mt-4.grid.grid-cols-1.md:grid-cols-3.gap-4
          [:div
           [ui/text-input {:id         (str "penalty-" id)
                           :label-text (tr [:fees/penalty-rate])
                           :type       :text
                           :disabled   disabled?
                           :required   allow-penalty?
                           :value      (:fee-template/penalty-rate fee-template)
                           :on-change  #(rf/dispatch-sync [::ft-events/change-field
                                                           id
                                                           :fee-template/penalty-rate
                                                           (-> % futil/tval)])}]]
          [:div
           [ui-select/select {:id           (str "penalty-rate-" id)
                              :class-name   "full-width-select"
                              :label-text   (tr [:fees/rate-type])
                              :disabled     disabled?
                              :allow-empty? false
                              :required     penalty-rate-filled?
                              :values       fee-model/rates
                              :value        (:fee-template/penalty-rate-type fee-template)
                              :on-change    #(>evt [::ft-events/change-field id :fee-template/penalty-rate-type %])
                              :val->str     (comp tr vector)}]]
          (when supports-grace-period?
            [:div.flex.items-center
             (cc/checkbox {:id         (str "allow-grace-period-" id)
                           :label-text (tr [:group-admin/grace-period])
                           :checked    (boolean (:fee-template/apply-grace-period? fee-template))
                           :disabled   disabled?
                           :on-change  #(>evt [::ft-events/change-field id
                                               :fee-template/apply-grace-period? %])})])]
         [divider]
         [:div.mt-4.grid.grid-cols-1.md:grid-cols-4.gap-4
          [:div
           [ui/text-input {:id         (str "interest-" id)
                           :label-text (tr [:fees/interest-rate])
                           :type       :text
                           :required   (not penalty-rate-filled?)
                           :disabled   disabled?
                           :value      (:fee-template/interest-rate fee-template)
                           :on-change  #(rf/dispatch-sync [::ft-events/change-field
                                                           id
                                                           :fee-template/interest-rate
                                                           (-> % futil/tval)])}]]
          [:div
           [ui-select/select {:id           (str "interest-rate-" id)
                              :class-name   "full-width-select"
                              :label-text   (tr [:fees/rate-type])
                              :disabled     disabled?
                              :allow-empty? false
                              :required     interest-filled?
                              :values       fee-model/rates
                              :value        (:fee-template/interest-rate-type fee-template)
                              :on-change    #(>evt [::ft-events/change-field id :fee-template/interest-rate-type %])
                              :val->str     (comp tr vector)}]]
          [:div
           [ui-select/select {:id           (str "interest-recurrence-" id)
                              :class-name   "full-width-select"
                              :label-text   (tr [:fees/recurrence])
                              :disabled     disabled?
                              :allow-empty? false
                              :required     interest-filled?
                              :values       fee-model/interest-recurrences
                              :value        (:fee-template/interest-recurrence fee-template)
                              :on-change    #(>evt [::ft-events/change-field id :fee-template/interest-recurrence %])
                              :val->str     (comp tr vector)}]]
          [:div
           [ui/number-input {:id         (str "interest-occurrence-limit-" id)
                             :label-text (tr [:fees/interest-occurrence-limit])
                             :required   false
                             :disabled   disabled?
                             :value      (:fee-template/interest-occurrence-limit fee-template)
                             :on-change  #(rf/dispatch-sync [::ft-events/change-field
                                                             id
                                                             :fee-template/interest-occurrence-limit
                                                             (let [val (-> % futil/tval js/parseInt)]
                                                               (when (pos? val) val))])}]]]]
        (ui/help (tr [:fees/interest-occurrence-limit-help]))])]))


(defn render-fee-template-editor
  "Renders the editor for a single fee template, old or new"
  [{:keys [tr disabled? id fee-template fee-variables]}]
  (let [creating-new?                           (= id :new)
        {:fee-template/keys [titles type group]
         phase              :bill/phase}        fee-template
        quantity-formula                        (<sub [::ft-subs/quantity-formula])
        unit-price-formula                      (<sub [::ft-subs/unit-price-formula])
        group-id                                (cond-> group ; group can be either entity (from backend) or plain id (unsaved changes)
                                                  (contains? group :db/id) :db/id)
        fee-integration?                        (<sub [::gas/has-feature? :group.feature/fee-integration])
        unit                                    (:fee-template/unit fee-template)]
    [eb/error-boundary
     [:div.col-span-2.grid-cols-2.gap-4.items-start
      [editor-grid
       [editor-title (tr [:fees/general])]
       [render-title (util/mmap id tr titles disabled?)]
       [render-phase (util/mmap id tr phase disabled?)]
       [render-price-formation-type (util/mmap id tr disabled? type)]
       (when (and (<sub [::gas/has-permit-type? #{:permit-type/L :permit-type/CR :permit-type/CE :permit-type/B
                                                  :permit-type/P :permit-type/PW :permit-type/BS :permit-type/LU}])
                  (ff/enabled? :feature-flag.id/grace-period?)
                  (ff/enabled? :feature-flag.id/penalty-and-interest)
                  (fee-model/penalty-allowed-for-phase? phase))
         [render-penalty (util/mmap id tr disabled? fee-template)])
       (when fee-integration?
         [render-external-id (util/mmap fee-template id group-id tr disabled?)])
       [render-advanced-options (util/mmap fee-template id group-id tr disabled? fee-integration?)]]

      (when (= type :fee-template/fixed)
        [render-fixed-formula (util/mmap tr disabled? id unit-price-formula quantity-formula)])

      (when (= type :fee-template/both-calculated)
        [:<>
         [render-quantity-formula (util/mmap tr disabled? id quantity-formula fee-variables)]
         [render-unit-settings (util/mmap tr disabled? id unit)]
         [render-unit-price-formula (util/mmap tr disabled? id unit-price-formula fee-variables)]
         [render-help tr fee-variables]])

      [:div.p-4.bg-white.grid.grid-cols-2.gap-4
       [render-save-cancel (util/mmap id tr creating-new? disabled?)]]]]))


(defn view [{:keys [tr]}]
  (let [creating-new? (<sub [::ft-subs/creating-new?])
        disabled?     (<sub [::ft-subs/editor-disabled?])
        fee-templates (<sub [::ft-subs/fee-template-list])
        fee-template  (<sub [::ft-subs/selected-fee-template])
        fee-variables (<sub [::ft-subs/available-fee-variables])
        current-id    (<sub [::ft-subs/selected-fee-template-id])]
    [eb/error-boundary
     [:div.grid.grid-cols-3.gap-4.p-4.bg-ui-light-gray
      [:h1.col-span-3.text-sm.font-bold.leading-none (tr [:fees/fee-templates])]
      [:div.col-span-1.py-4.px-2.bg-white
       [columns/column-select (p/assoc-if {:tr           tr
                                           :title        :fees/fee-templates
                                           :options      fee-templates
                                           :on-change    #(>evt [::ft-events/select-fee-template %])
                                           :height       "600px"
                                           :transparent? true
                                           :disabled?    creating-new?}
                                          :actions (when-not disabled?
                                                     [{:title    (tr [:fees/new-fee-item])
                                                       :on-click #(>evt [::ft-events/create-new-fee-template])}]))]]
      (if (or fee-template creating-new?)
        [render-fee-template-editor (util/mmap {:id           current-id
                                                :fee-template (<sub [::ft-subs/fee-attrs-with-changes])}
                                               fee-variables tr disabled?)]
        [:div.col-span-2.p-4.bg-white.flex.justify-center.items-center
         [:span.font-bold.uppercase.tracking-wider.text-gray-500
          (tr [:fees/select-or-create-a-template])]])]]))
